import { z } from 'zod';
/**
 * Accent-colour validation (`technical.md`, Shared validation functions).
 * The same functions run on the server and in the dashboard so the rules
 * cannot drift (`patterns/api-conventions.md`, 2).
 */
/** Parses a `#RRGGBB` hex string into [R, G, B] in 0-255. */
function hexToRgb(hex) {
    const match = /^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(hex);
    if (!match)
        return null;
    return [parseInt(match[1], 16), parseInt(match[2], 16), parseInt(match[3], 16)];
}
/**
 * WCAG 2.1 relative luminance of a colour.
 * https://www.w3.org/TR/WCAG21/#dfn-relative-luminance
 */
function relativeLuminance(r, g, b) {
    const [rs, gs, bs] = [r, g, b].map((c) => {
        const s = c / 255;
        return s <= 0.04045 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
    });
    return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs;
}
/**
 * Computes the WCAG 2.1 contrast ratio of the given hex colour against
 * `#FFFFFF`. Returns `0` if the hex string is invalid.
 */
export function contrastRatioOnWhite(hex) {
    const rgb = hexToRgb(hex);
    if (!rgb)
        return 0;
    const l1 = 1; // white
    const l2 = relativeLuminance(...rgb);
    return (Math.max(l1, l2) + 0.05) / (Math.min(l1, l2) + 0.05);
}
/**
 * Returns `true` when the colour keeps >= 4.5:1 contrast with white text
 * (the Borrowed Accent Rule, `patterns/design-system.md`).
 */
export function meetsAccentContrast(hex) {
    return contrastRatioOnWhite(hex) >= 4.5;
}
/**
 * Normalizes a hex colour input: trims, uppercases the hex digits, and
 * ensures the `#` prefix. Returns the 7-character `#RRGGBB` string, or
 * `null` when the input is not a valid hex colour.
 */
export function parseHexColour(input) {
    let value = input.trim();
    // Accept without #
    if (/^[0-9a-f]{6}$/i.test(value)) {
        value = `#${value}`;
    }
    // Validate #RRGGBB
    if (!/^#[0-9a-f]{6}$/i.test(value))
        return null;
    return value.toUpperCase();
}
/**
 * A zod schema for a hex colour that meets the 4.5:1 accent contrast
 * requirement. The refine message includes the measured ratio.
 */
export const hexColourSchema = z
    .string()
    .transform((value) => parseHexColour(value))
    .refine((value) => value !== null, {
    message: 'Enter a valid hex colour, e.g. #1F6F5C.',
})
    .refine((value) => meetsAccentContrast(value), {
    message: 'This colour does not meet the 4.5:1 contrast requirement with white text. Choose a darker colour.',
});
//# sourceMappingURL=color.js.map